import React, { useState, useEffect, useRef, useContext } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { Form, InputGroup } from 'react-bootstrap';
import { ChevronDown } from 'react-bootstrap-icons';
import { AuthContext } from '../../contexts/AuthContext';

const TickerComboBox = ({ 
  value, 
  onChange, 
  placeholder = "Enter ticker symbol...",
  className = "",
  required = false,
  disabled = false,
  isInvalid = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [inputValue, setInputValue] = useState(value || '');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  // Use ticker options from user settings (already loaded)
  const { user } = useContext(AuthContext);
  const tickerData = user?.tickerOptions;
  const isLoading = !tickerData;

  // Update options when data is loaded
  useEffect(() => {
    if (tickerData?.suggestions) {
      setOptions(tickerData.suggestions);
    }
  }, [tickerData]);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter options when input changes
  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter(option =>
        option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setFilteredOptions(options.slice(0, 10));
    }
  }, [inputValue, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase();
    setInputValue(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleOptionSelect = (option) => {
    const tickerSymbol = option.value;
    setInputValue(tickerSymbol);
    setIsOpen(false);
    onChange(tickerSymbol);
    inputRef.current?.blur();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getOptionIcon = (type) => {
    return type === 'futures' ? '📈' : '📊';
  };

  return (
    <div className={`position-relative ${className} ticker-combobox-dark`} ref={dropdownRef}>
      <InputGroup>
        <Form.Control
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          isInvalid={isInvalid}
          className="border-end-0"
        />
        <InputGroup.Text 
          className="border-start-0 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: 'pointer' }}
        >
          {isLoading ? (
            <LoadingSpinner small />
          ) : (
            <ChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </InputGroup.Text>
      </InputGroup>

      {isOpen && (
        <div className="position-absolute w-100 mt-1 border rounded shadow-sm" style={{ zIndex: 9999, maxHeight: '200px', overflowY: 'auto' }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className="px-3 py-2 cursor-pointer ticker-dropdown-item"
                onClick={() => handleOptionSelect(option)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center">
                  <span className="me-2">{getOptionIcon(option.type)}</span>
                  <div>
                    <div className="fw-bold">{option.value}</div>
                    <small className="text-muted">{option.label.split(' - ')[1]}</small>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-muted" style={{ fontSize: '0.85rem', opacity: 0.7 }}>
              {inputValue ? 'Press Enter to use' : 'Type to search...'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TickerComboBox; 